/**
 * Copies the authoritative room state into the scene bus once per frame, and
 * interpolates remote projectiles between server ticks.
 *
 * The server simulates at 30 Hz and the client renders at 60; without this the
 * duel would visibly stutter. Positions are extrapolated along the last known
 * velocity and then eased toward each fresh server position, which keeps a
 * fast bolt smooth without letting it drift away from where the server thinks
 * it is (and therefore from where the parry window applies).
 */
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { COMBAT } from '@spell/shared-types';
import { net } from '../networking/net';
import { scene as bus, type ProjectileView } from './sceneBus';

interface Smoothed {
  x: number;
  y: number;
  z: number;
}

export function NetSync() {
  const smoothed = useRef(new Map<string, Smoothed>());
  const pool = useRef<ProjectileView[]>([]);

  useFrame((_, rawDelta) => {
    const room = net.room;
    // The practice range is a real room, so it is fed from here too; only the
    // offline tutorial writes its own projectiles into the bus.
    if (!room || (bus.mode !== 'match' && bus.mode !== 'practice')) return;
    const state = room.state as unknown as RoomStateShape;
    if (!state?.players) return;

    const delta = Math.min(rawDelta, 0.1);
    const selfId = net.sessionId;

    const self = state.players.get(selfId);
    const opponent = findOpponent(state, selfId);

    const serverTime = state.serverTime || Date.now();

    if (self) {
      bus.selfSide = self.side;
      bus.selfHp = self.hp;
      bus.selfMaxHp = self.maxHp;
      bus.selfMana = self.mana;
      bus.selfMaxMana = self.maxMana;
      bus.selfShield = self.shield;
      // Handed to the input loop rather than applied here: this is the truth
      // the local prediction is eased onto, never the camera position itself.
      bus.serverSelfX = self.x;
      bus.serverSelfZ = self.z;
      // The prediction has to refuse to walk under exactly the conditions the
      // room refuses to walk it, or every stun would end in a snap backwards.
      bus.selfStunned = serverTime < self.stunnedUntil;
      bus.selfSlowed = serverTime < self.slowUntil;
    }

    bus.oppPresent = Boolean(opponent);
    if (opponent) {
      bus.oppSide = opponent.side;
      bus.oppHp = opponent.hp;
      bus.oppMaxHp = opponent.maxHp;
      bus.oppMana = opponent.mana;
      bus.oppMaxMana = opponent.maxMana;
      bus.oppCasting = opponent.casting;
      bus.oppCastProgress = opponent.castProgress;
      bus.oppCastColor = opponent.castColor || '#ff5a3c';
      bus.oppStunned = serverTime < opponent.stunnedUntil;
      // Eased, not dead-reckoned: a mage walks slowly enough that catching up
      // reads as walking, where extrapolating a direction would overshoot every
      // time they changed their mind.
      const k = Math.min(1, delta * 10);
      bus.oppX += (opponent.x - bus.oppX) * k;
      bus.oppZ += (opponent.z - bus.oppZ) * k;
    }

    // --- projectiles ---------------------------------------------------
    const views = pool.current;
    views.length = 0;
    const alive = new Set<string>();

    state.projectiles.forEach((p) => {
      alive.add(p.id);
      let s = smoothed.current.get(p.id);
      if (!s) {
        s = { x: p.x, y: p.y, z: p.z };
        smoothed.current.set(p.id, s);
      } else {
        // Dead-reckon along the server velocity, then converge on the truth.
        s.x += p.vx * delta;
        s.y += p.vy * delta;
        s.z += p.vz * delta;
        const k = Math.min(1, delta * 12);
        s.x += (p.x - s.x) * k;
        s.y += (p.y - s.y) * k;
        s.z += (p.z - s.z) * k;
      }

      const incoming = p.targetId === selfId;
      views.push({
        id: p.id,
        x: s.x,
        y: s.y,
        z: s.z,
        vx: p.vx,
        vy: p.vy,
        vz: p.vz,
        radius: p.radius,
        color: p.color,
        accentColor: p.accentColor,
        shape: p.shape,
        element: p.element,
        homing: p.homing,
        incoming,
        parryable: p.parryable,
        eta: Math.max(0, (p.impactAt - serverTime) / 1000),
        resolveIn: p.resolveAt > 0 ? Math.max(0, (p.resolveAt - serverTime) / 1000) : 0,
        armUntil: p.armUntil,
        complexity: p.complexity,
      });
    });

    for (const key of smoothed.current.keys()) {
      if (!alive.has(key)) smoothed.current.delete(key);
    }

    bus.projectiles = views;
  });

  return null;
}

function findOpponent(state: RoomStateShape, selfId: string): PlayerShape | undefined {
  let found: PlayerShape | undefined;
  state.players.forEach((p, key) => {
    if (key !== selfId) found = p;
  });
  return found;
}

/* ------------------------------------------------------------------ */
/* Structural view of the synced schema                                */
/* ------------------------------------------------------------------ */

interface PlayerShape {
  sessionId: string;
  side: number;
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  shield: number;
  casting: boolean;
  castProgress: number;
  castColor: string;
  stunnedUntil: number;
  slowUntil: number;
}

interface ProjectileShape {
  id: string;
  targetId: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
  color: string;
  accentColor: string;
  shape: string;
  element: string;
  homing: number;
  parryable: boolean;
  impactAt: number;
  resolveAt: number;
  armUntil: number;
  complexity: number;
}

interface RoomStateShape {
  serverTime: number;
  players: { get(id: string): PlayerShape | undefined; forEach(fn: (p: PlayerShape, key: string) => void): void };
  projectiles: { forEach(fn: (p: ProjectileShape) => void): void };
}

export const DUEL_HALF_DISTANCE = COMBAT.DUEL_DISTANCE / 2;
